import type { IconToken } from '../icons/index';
/** Things you can drive. Cooter's Used Cars sells everything here but the Camaro. */

export type RideId = 'camaro' | 'lifted' | 'donk' | 'elcamino' | 'crownvic' | 'van' | 'atv' | 'mower';

export interface RideStats {
  /** m/s. */
  maxSpeed: number;
  accel: number;
  steer: number;
  /** Collision circle radius, and how far front and back the two circles sit. */
  radius: number;
  axle: number;
  /** How well it handles mud: 0 = stuck like a pig in it, 1 = made for it. */
  mud: number;
  /** Camera height while driving. */
  seat: number;
}

export interface RideDef {
  id: RideId;
  name: string;
  icon: IconToken;
  /** 0 = not for sale (the Camaro's an heirloom). */
  price: number;
  desc: string;
  /** Short brag line for the shop card. */
  pitch: string;
  stats: RideStats;
}

export const RIDES: readonly RideDef[] = [
  {
    id: 'camaro',
    name: "Uncle's '87 Camaro",
    icon: ':car:',
    price: 0,
    desc: 'T-tops, a mullet in car form, and a tape deck stuck on Skynyrd.',
    pitch: 'Fast on pavement. Useless in mud.',
    stats: { maxSpeed: 21, accel: 9, steer: 1.9, radius: 1.15, axle: 1.3, mud: 0.25, seat: 1.2 },
  },
  {
    id: 'lifted',
    name: 'Lifted 4x4 "Big Bubba"',
    icon: ':lifted-truck:',
    price: 1800,
    desc: 'Six-inch lift, 38s, a light bar you can see from space. You need a ladder to get in.',
    pitch: 'King of the mud bog.',
    stats: { maxSpeed: 19, accel: 8.5, steer: 1.6, radius: 1.35, axle: 1.6, mud: 1, seat: 1.7 },
  },
  {
    id: 'donk',
    name: 'Candy Paint Donk',
    icon: ':donk:',
    price: 1400,
    desc: "A '75 Caprice on 26-inch rims. The paint job cost more than the car. Twice.",
    pitch: 'Turns heads. Does not turn corners.',
    stats: { maxSpeed: 23, accel: 8, steer: 1.45, radius: 1.2, axle: 1.4, mud: 0.15, seat: 1.5 },
  },
  {
    id: 'elcamino',
    name: "'79 El Camino",
    icon: ':el-camino:',
    price: 900,
    desc: 'Business in the front, party in the back. The mullet of automobiles.',
    pitch: 'Half car. Half truck. All class.',
    stats: { maxSpeed: 20, accel: 8.5, steer: 1.8, radius: 1.15, axle: 1.35, mud: 0.4, seat: 1.25 },
  },
  {
    id: 'crownvic',
    name: 'Retired Police Interceptor',
    icon: ':police-car:',
    price: 700,
    desc: '280,000 miles, a push bar and a spotlight. Folks still pull over for you.',
    pitch: 'Fast. Folks get out of your way.',
    stats: { maxSpeed: 24, accel: 9.5, steer: 1.75, radius: 1.15, axle: 1.35, mud: 0.3, seat: 1.25 },
  },
  {
    id: 'van',
    name: 'Conversion Van With a Wolf Mural',
    icon: ':wolf-van:',
    price: 600,
    desc: 'Shag carpet, a bench seat that folds into a bed, and a wolf howling at a moon on the side.',
    pitch: 'Sleeps six. Smells like twelve.',
    stats: { maxSpeed: 17, accel: 6.5, steer: 1.5, radius: 1.2, axle: 1.45, mud: 0.35, seat: 1.6 },
  },
  {
    id: 'atv',
    name: 'Three-Wheeler (Recalled in 1988)',
    icon: ':three-wheeler:',
    price: 500,
    desc: 'Banned for being too dangerous, which is exactly why you want it.',
    pitch: 'Quick, nimble and great in mud.',
    stats: { maxSpeed: 18, accel: 11, steer: 2.4, radius: 0.75, axle: 0.55, mud: 0.85, seat: 1.0 },
  },
  {
    id: 'mower',
    name: 'Riding Mower (Street Legal-ish)',
    icon: ':riding-mower:',
    price: 250,
    desc: "Top speed of a brisk jog. The sheriff's given up arguing about it.",
    pitch: "Cheap. Slow. Can't get stuck if you never go fast.",
    stats: { maxSpeed: 9, accel: 6, steer: 2.3, radius: 0.75, axle: 0.5, mud: 0.6, seat: 0.95 },
  },
];

export const RIDE_IDS: readonly RideId[] = RIDES.map((r) => r.id);

export const rideDef = (id: string): RideDef => RIDES.find((r) => r.id === id) ?? (RIDES[0] as RideDef);

export const isRide = (id: unknown): id is RideId =>
  typeof id === 'string' && (RIDE_IDS as string[]).includes(id);
